import React from 'react';
import axios from 'axios';
import {
  ButtonBase,
  Popover,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/system';
import { grey } from '@mui/material/colors';

import { skipBackendProbeForApp } from 'lib/backendAppProbe';
import { DARK, FEATURES_QR_CODE_PATH } from 'lib/config';
import Paths from 'lib/paths';
import { useAppInitiator } from 'providers/AppInitiator';
import { useTranslation } from 'react-i18next';

const Wrapper = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const WrapperInt = styled('div')({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  padding: '0 10px',
});

const WrapperIntMobile = styled('div')({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  padding: '0 5px',
});

const QrcodeView = ({ type, app }: { type: string; app: string }) => {
  const { boot } = useAppInitiator();
  const { t } = useTranslation();
  const matches = useMediaQuery('(max-width:599px)');

  const [allowed, setAllowed] = React.useState<boolean | null>(null);
  const [anchorEl, setAnchorEl] =
    React.useState<HTMLButtonElement | null>(null);

  const appCode = React.useMemo(
    () => (typeof app === 'string' ? app.trim().toLowerCase() : ''),
    [app],
  );

  React.useEffect(() => {
    let cancelled = false;

    if (skipBackendProbeForApp(appCode)) {
      setAllowed(false);
      return undefined;
    }

    setAllowed(null);

    const base = Paths.join(
      boot.backends.api.baseUrl,
      FEATURES_QR_CODE_PATH,
    );
    const url = Paths.join(base, appCode);

    (async () => {
      try {
        const response = await axios.get(url, {
          maxRedirects: 5,
          validateStatus: () => true,
        });
        if (!cancelled) {
          setAllowed(response.status === 200);
        }
      } catch {
        if (!cancelled) setAllowed(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [appCode, boot.backends.api.baseUrl]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? 'qrcode-popover' : undefined;

  if (allowed !== true) return null;

  const WrapperComp = matches ? WrapperIntMobile : WrapperInt;
  const textColor =
    open
      ? '#1A416B'
      : type === DARK
        ? grey[50]
        : 'rgba(0, 0, 0, 0.54)';

  return (
    <>
      <WrapperComp>
        <ButtonBase
          aria-describedby={popoverId}
          onClick={handleClick}
          sx={{
            border: 0,
            borderRadius: 1,
            px: 1,
            py: 0.5,
            background: open ? '#fff' : 'transparent',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: textColor,
              fontSize: '14px',
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            {t('qrcode', 'QR code')}
          </Typography>
        </ButtonBase>
      </WrapperComp>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Wrapper direction="column" sx={{ padding: '16px' }}>
          <Typography variant="body2">{`qrcode ${appCode}`}</Typography>
        </Wrapper>
      </Popover>
    </>
  );
};

export default QrcodeView;
